import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Sparkles, Bot } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';
import { getEmojiFromCode } from '@/pages/Assessor/Settings';
import { FloatingAssessorChat } from './FloatingAssessorChat';

const STORAGE_KEY = 'assessor-floating-hidden';
const POSITION_KEY = 'assessor-floating-position';

export const FloatingAssessorIcon: React.FC = () => {
  const { user } = useAuth();
  const [hidden, setHidden] = useState(() => {
    return sessionStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [chatOpen, setChatOpen] = useState(false);
  const [avatar, setAvatar] = useState<string | null>(null);
  const [assistantName, setAssistantName] = useState('Assessor');
  const [enabled, setEnabled] = useState(true); // Aparece por padrão
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Drag state
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem(POSITION_KEY);
      if (saved) return JSON.parse(saved);
    } catch {}
    // Posição padrão: lado direito, centro vertical
    return { x: window.innerWidth - 80, y: Math.round(window.innerHeight / 2 - 28) };
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number; posX: number; posY: number } | null>(null);
  const hasMoved = useRef(false);
  const iconRef = useRef<HTMLDivElement>(null);

  // Load assistant settings
  useEffect(() => {
    if (!user?.id) {
      setSettingsLoaded(false);
      return;
    }
    const load = async () => {
      try {
        const { data } = await supabase
          .from('assistant_settings')
          .select('assistant_avatar, assistant_name, enabled')
          .eq('user_id', user.id)
          .maybeSingle();
        if (data) {
          setAvatar(data.assistant_avatar);
          setAssistantName(data.assistant_name || 'Assessor');
          setEnabled(data.enabled ?? true);
        } else {
          // Sem registro = habilitado por padrão
          setEnabled(true);
        }
      } catch (err) {
        console.error('Erro ao carregar settings do assessor:', err);
        // Em caso de erro, mostra o assessor por padrão
        setEnabled(true);
      }
      setSettingsLoaded(true);
    };
    load();
  }, [user?.id]);

  // Clamp position to viewport
  const clamp = useCallback((pos: { x: number; y: number }) => {
    const size = 56;
    return {
      x: Math.max(0, Math.min(pos.x, window.innerWidth - size)),
      y: Math.max(0, Math.min(pos.y, window.innerHeight - size)),
    };
  }, []);

  // Save position
  useEffect(() => {
    localStorage.setItem(POSITION_KEY, JSON.stringify(position));
  }, [position]);

  // Pointer handlers for drag
  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    hasMoved.current = false;
    dragStart.current = { x: e.clientX, y: e.clientY, posX: position.x, posY: position.y };
    setIsDragging(true);
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  }, [position]);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragStart.current || !isDragging) return;
    const dx = e.clientX - dragStart.current.x;
    const dy = e.clientY - dragStart.current.y;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      hasMoved.current = true;
    }
    setPosition(clamp({
      x: dragStart.current.posX + dx,
      y: dragStart.current.posY + dy,
    }));
  }, [isDragging, clamp]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    setIsDragging(false);
    if (!hasMoved.current) {
      setChatOpen(prev => !prev); // Toggle: abre ou fecha
    }
    dragStart.current = null;
  }, []);

  // Recalc on resize
  useEffect(() => {
    const onResize = () => setPosition(prev => clamp(prev));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [clamp]);

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setHidden(true);
    setChatOpen(false);
    sessionStorage.setItem(STORAGE_KEY, 'true');
  };

  if (hidden || !settingsLoaded || !enabled || !user) return null;

  const renderAvatar = () => {
    if (avatar?.startsWith('emoji:')) {
      return (
        <div className="w-full h-full bg-background flex items-center justify-center text-2xl">
          {getEmojiFromCode(avatar)}
        </div>
      );
    }
    if (avatar) {
      return <img src={avatar} alt="Assessor" className="w-full h-full object-cover bg-background" />;
    }
    return (
      <div className="w-full h-full bg-background flex items-center justify-center">
        <Sparkles className="h-6 w-6 text-primary" />
      </div>
    );
  };

  return (
    <>
      {/* Chat panel */}
      {chatOpen && (
        <FloatingAssessorChat
          position={position}
          onClose={() => setChatOpen(false)}
          assistantName={assistantName}
          assistantAvatar={avatar}
        />
      )}

      {/* Draggable icon */}
      <div
        ref={iconRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          zIndex: 10001,
          touchAction: 'none',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        className="group"
      >
        {/* Close X button */}
        <button
          onClick={handleClose}
          onPointerDown={e => e.stopPropagation()}
          className="absolute -top-2 -right-2 w-6 h-6 min-w-[1.5rem] min-h-[1.5rem] rounded-full flex items-center justify-center opacity-100 z-10 p-0 leading-[0] border-none cursor-pointer shrink-0"
          style={{ background: 'hsl(var(--destructive))', color: 'white', boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
          aria-label="Fechar assessor"
        >
          <X className="w-3 h-3" strokeWidth={3} />
        </button>

        {/* Avatar circle */}
        <div
          className={`w-14 h-14 rounded-full overflow-hidden shadow-lg border-2 bg-background transition-all duration-200 ${
            chatOpen 
              ? 'border-primary ring-2 ring-primary/30' 
              : 'border-background hover:border-primary/50 hover:shadow-xl hover:scale-105'
          }`}
        >
          {renderAvatar()}
        </div>
      </div>
    </>
  );
};
